import { z } from 'zod';
import { SanityDocumentSchema } from '../sanity';
import { ComponentType } from '$lib/sanity/types';
import { NewsBasicPostSchema, NewsPostSchema } from '../entities/NewsPost';
import { PageTreeHierarchyItemSchema } from '../entities/PageTree';
import { PageSchema } from '../entities/Page';
import { DocumentSchema } from '../entities/Document';
import { ExternalContentSchema } from '../entities/ExternalContent';
import { ImageAssetSchema } from './Image';

export const NewsCardContentSchema = z.union([
  NewsPostSchema,
  NewsBasicPostSchema,
  DocumentSchema,
  PageSchema.extend({
    hierarchyItems: z
      .array(PageTreeHierarchyItemSchema)
      .nullish()
      .transform((x) => x ?? [])
  }),
  ExternalContentSchema
]);

export const NewsCardContentArraySchema = z.array(NewsCardContentSchema);

export const NewsCardSchema = SanityDocumentSchema.extend({
  _type: z.literal(ComponentType.NewsCard),
  name: z.string(),
  headlineSize: z.union([
    z.literal('h1'),
    z.literal('h2'),
    z.literal('h3'),
    z.literal('h4'),
    z.literal('h5')
  ]),
  content: z.lazy(() => NewsCardContentSchema.nullish()),
  variant: z.object({
    type: z.string(),
    topElement: z.string().optional(),
    imagePosition: z.string().optional(),
    alignment: z.string().optional()
  }),
  badge: z.union([
    z.literal('none'),
    z.literal('topRight'),
    z.literal('topLeft'),
    z.literal('bottomLeft'),
    z.literal('bottomRight')
  ]),
  buttonText: z.string().nullable().optional(),
  aspectRatio: z
    .string()
    .nullish()
    .transform((x) => x ?? '1:1'),
  imageMask: z.string().nullish().default('square'),
  itemId: z.string().optional().nullable()
});

const NewsCardImageSchema = z.object({
  image: ImageAssetSchema.nullable().optional(),
  aspectRatio: z.string().optional(),
  imageMask: z.string().nullable().optional()
});

export type NewsCardContent = z.infer<typeof NewsCardContentSchema>;
export type NewsCard = z.infer<typeof NewsCardSchema>;
export type NewsCardContentArray = z.infer<typeof NewsCardContentArraySchema>;
export type NewsCardImage = z.infer<typeof NewsCardImageSchema>;
